// modals/ObjectivesModal.js - VERSION CORRIGÉE
import { X, RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useCategoryObjectives } from '../../hooks-suggestion/useObjectiveSuggestions';
import ObjectiveForm from './ObjectiveForm';
import ObjectiveSuggestions from './ObjectiveSuggestions';

const ObjectivesModal = ({
    isOpen,
    onClose,
    onSave, // Ce callback sera utilisé au lieu des hooks
    editObjectif = null
}) => {
    const [formData, setFormData] = useState({
        title: '',
        category: '',
        customCategory: '',
        current: '',
        target: '',
        timeline: '',
        timelineUnit: 'mois',
        description: '',
        valueUnit: '',
        valueType: 'number',
        priority: 'medium'
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const modalRef = useRef(null);

    // Hook pour charger les suggestions de la catégorie sélectionnée
    const {
        categoryObjectives: categorySuggestions,
        loading: loadingSuggestions,
        error: errorSuggestions
    } = useCategoryObjectives(formData.category);

    // Options de catégories immobilières avec unités suggérées
    const categoryOptions = [
        {
            value: 'patrimoine-immobilier',
            label: 'Patrimoine Immobilier',
            shortLabel: 'Patrimoine',
            suggestedUnits: [
                { value: '€', label: 'Euros (€)', type: 'number' },
                { value: 'k€', label: 'Milliers d\'euros (k€)', type: 'number' },
                { value: 'biens', label: 'Nombre de biens', type: 'number' }
            ],
            defaultUnit: '€'
        },
        {
            value: 'revenus-locatifs',
            label: 'Cash Flow Immobilier',
            shortLabel: 'Cash Flow',
            suggestedUnits: [
                { value: '€/mois', label: 'Euros par mois (€/mois)', type: 'number' },
                { value: '€/an', label: 'Euros par an (€/an)', type: 'number' },
                { value: '%', label: 'Pourcentage (%)', type: 'percentage' }
            ],
            defaultUnit: '€/mois'
        },
        {
            value: 'strategies-immobilieres',
            label: 'Compétences & Stratégies',
            shortLabel: 'Compétences',
            suggestedUnits: [
                { value: '/10', label: 'Note sur 10 (/10)', type: 'number' },
                { value: '%', label: 'Pourcentage (%)', type: 'percentage' },
                { value: 'niveau', label: 'Niveau de maîtrise', type: 'text' },
                { value: 'formations', label: 'Nombre de formations', type: 'number' }
            ],
            defaultUnit: '/10'
        },
        {
            value: 'reseau-visibilite',
            label: 'Réseau & Visibilité',
            shortLabel: 'Réseau',
            suggestedUnits: [
                { value: 'contacts', label: 'Nombre de contacts', type: 'number' },
                { value: 'followers', label: 'Nombre d\'abonnés', type: 'number' },
                { value: '%', label: 'Taux d\'engagement (%)', type: 'percentage' },
                { value: 'événements', label: 'Nombre d\'événements', type: 'number' }
            ],
            defaultUnit: 'contacts'
        },
        {
            value: 'autres',
            label: 'Autre (personnalisé)',
            shortLabel: 'Autre',
            suggestedUnits: [
                { value: 'custom', label: 'Unité personnalisée', type: 'text' },
                { value: '€', label: 'Euros (€)', type: 'number' },
                { value: '%', label: 'Pourcentage (%)', type: 'percentage' },
                { value: 'unités', label: 'Nombre d\'unités', type: 'number' }
            ],
            defaultUnit: 'custom'
        }
    ];

    // Options de priorité
    const priorityOptions = [
        { value: 'low', label: 'Basse', color: 'text-gray-600' },
        { value: 'medium', label: 'Moyenne', color: 'text-gray-600' },
        { value: 'high', label: 'Haute', color: 'text-gray-600' }
    ];

    // Fonction pour mapper la catégorie de la base vers les options du formulaire
    const mapCategoryToFormValue = (dbCategory) => {
        const categoryMapping = {
            'Patrimoine Immobilier': 'patrimoine-immobilier',
            'Cash Flow Immobilier': 'revenus-locatifs',
            'Compétences & Stratégies': 'strategies-immobilieres',
            'Réseau & Visibilité': 'reseau-visibilite'
        };
        if (categoryOptions.find(cat => cat.value === dbCategory)) {
            return dbCategory;
        }
        return categoryMapping[dbCategory] || 'autres';
    };

    // Fonction pour mapper les nouveaux statuts
    const mapStatusDisplay = (status) => {
        const statusMapping = {
            'a_faire': { label: 'À faire', color: 'bg-gray-100 text-gray-800' },
            'en_cours': { label: 'En cours', color: 'bg-blue-100 text-blue-800' },
            'fait': { label: 'Fait', color: 'bg-green-100 text-green-800' },
            'annule': { label: 'Annulé', color: 'bg-red-100 text-red-800' }
        };
        return statusMapping[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
    };

    // Réinitialiser ou charger le formulaire selon le mode (création/édition)
    useEffect(() => {
        if (isOpen) {
            if (editObjectif) {
                setFormData({
                    title: editObjectif.title || '',
                    category: mapCategoryToFormValue(editObjectif.category || ''),
                    customCategory: editObjectif.custom_category || '',
                    current: editObjectif.current_value?.toString() || '',
                    target: editObjectif.target_value?.toString() || '',
                    timeline: editObjectif.timeline?.toString() || '',
                    timelineUnit: editObjectif.timeline_unit || 'mois',
                    description: editObjectif.description || '',
                    valueUnit: editObjectif.value_unit || '',
                    valueType: editObjectif.value_type || 'number',
                    priority: editObjectif.priority || 'medium'
                });
            } else {
                setFormData({
                    title: '',
                    category: '',
                    customCategory: '',
                    current: '',
                    target: '',
                    timeline: '',
                    timelineUnit: 'mois',
                    description: '',
                    valueUnit: '',
                    valueType: 'number',
                    priority: 'medium'
                });
            }
            setErrors({});
        }
    }, [isOpen, editObjectif]);

    // Gestion de la fermeture avec Escape
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    const handleInputChange = (field, value) => {
        setFormData(prev => {
            const newData = {
                ...prev,
                [field]: value
            };
            // Auto-sélection de l'unité par défaut lors du changement de catégorie
            if (field === 'category' && value !== 'autres') {
                const selectedCategory = categoryOptions.find(cat => cat.value === value);
                if (selectedCategory && selectedCategory.defaultUnit) {
                    const defaultUnitData = selectedCategory.suggestedUnits.find(unit => unit.value === selectedCategory.defaultUnit);
                    newData.valueUnit = selectedCategory.defaultUnit;
                    newData.valueType = defaultUnitData?.type || 'number';
                }
            }
            return newData;
        });

        // Effacer l'erreur du champ modifié
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    // Fonction pour appliquer une suggestion d'objectif
    const applySuggestion = (suggestion) => {
        const selectedCategoryData = categoryOptions.find(cat => cat.value === formData.category);
        const suggestionUnit = suggestion.unit || selectedCategoryData?.defaultUnit || '';
        const unitData = selectedCategoryData?.suggestedUnits?.find(unit => unit.value === suggestionUnit);

        setFormData(prev => ({
            ...prev,
            title: suggestion.name,
            current: prev.current || (suggestion.valueType === 'text' ? '' : 0),
            target: suggestion.target?.toString() || '',
            timeline: suggestion.timeline?.toString() || '',
            timelineUnit: suggestion.timelineUnit || 'mois',
            description: suggestion.description || prev.description,
            valueUnit: suggestionUnit,
            valueType: suggestion.valueType || unitData?.type || 'number',
            priority: suggestion.priority || 'medium'
        }));
    };

    // Validation du formulaire
    const validateForm = () => {
        const newErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Le titre est requis';
        }

        if (!formData.category) {
            newErrors.category = 'La catégorie est requise';
        }

        if (!formData.target || parseFloat(formData.target) <= 0) {
            newErrors.target = 'La valeur cible doit être supérieure à 0';
        }

        if (!formData.timeline || parseInt(formData.timeline) <= 0) {
            newErrors.timeline = 'La timeline doit être supérieure à 0';
        }

        if (formData.valueType === 'number' && formData.current && parseFloat(formData.current) < 0) {
            newErrors.current = 'La valeur actuelle ne peut pas être négative';
        }

        if (!formData.priority || !['low', 'medium', 'high'].includes(formData.priority)) {
            newErrors.priority = 'La priorité est requise';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const calculateMonthlyRequired = (gap, timeline, unit) => {
        let months = timeline;
        if (unit === 'semaines') {
            months = timeline / 4.33;
        } else if (unit === 'années') {
            months = timeline * 12;
        }
        return months > 0 ? gap / months : 0;
    };

    // Handler simplifié qui utilise le callback onSave
    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setErrors(prev => ({ ...prev, backend: null }));

        try {
            // Préparer les données pour le backend
            const objectiveData = {
                title: formData.title,
                description: formData.description || '',
                category: formData.category,
                custom_category: formData.category === 'autres' ? formData.customCategory : null,
                current_value: parseFloat(formData.current) || 0,
                target_value: parseFloat(formData.target),
                value_unit: formData.valueUnit || '',
                value_type: formData.valueType || 'number',
                timeline: parseInt(formData.timeline),
                timeline_unit: formData.timelineUnit || 'mois',
                priority: formData.priority || 'medium',
                progress_bar_color: '#016666',
                trend: 'up'
            };

            console.log('💾 Sauvegarde objectif via callback onSave...');

            // Utiliser le callback onSave du parent qui gère la logique CRUD
            const result = await onSave(objectiveData);

            if (result && result.success) {
                console.log('✅ Objectif sauvegardé avec succès');
                onClose();
            } else {
                throw new Error(result?.error || 'Erreur lors de la sauvegarde');
            }
        } catch (error) {
            console.error('❌ Erreur sauvegarde objectif:', error);
            setErrors(prev => ({
                ...prev,
                backend: error.message || 'Une erreur est survenue lors de la sauvegarde'
            }));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    const selectedCategory = categoryOptions.find(cat => cat.value === formData.category);

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div
                ref={modalRef}
                className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[calc(100vh-2rem)] overflow-hidden flex flex-col"
            >
                {/* Header épuré */}
                <div className="bg-gray-100 border-b border-gray-200 px-8 py-3 flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <h2 className="text-2xl font-medium text-gray-900 tracking-tight">
                                {editObjectif ? 'Modifier l\'objectif' : 'Ajouter un nouvel objectif'}
                            </h2>
                            {isSubmitting && (
                                <RefreshCw className="w-4 h-4 ml-3 animate-spin text-gray-500" />
                            )}
                        </div>
                        <button
                            onClick={onClose}
                            disabled={isSubmitting}
                            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Sélection de catégorie en haut */}
                <div className="px-8 py-6 border-b border-gray-100 flex-shrink-0">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                        Choisissez une catégorie d'objectif
                    </h3>
                    <div className="grid grid-cols-5 gap-3">
                        {categoryOptions.map((category) => (
                            <button
                                key={category.value}
                                type="button"
                                onClick={() => handleInputChange('category', category.value)}
                                className={`px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                                    formData.category === category.value
                                        ? 'bg-[#c8d7d6] text-gray-800'
                                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                }`}
                                disabled={isSubmitting}
                            >
                                {category.shortLabel}
                            </button>
                        ))}
                    </div>
                    {errors.category && (
                        <p className="mt-2 text-sm text-red-600">{errors.category}</p>
                    )}
                </div>

                {/* Contenu principal - affiché seulement si une catégorie est sélectionnée */}
                {formData.category && (
                    <div className="flex-1 overflow-y-auto">
                        <div className="p-8 pb-12">
                            {/* Affichage des erreurs backend */}
                            {errors.backend && (
                                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                                    <div className="flex">
                                        <div className="ml-3">
                                            <h3 className="text-sm font-medium text-red-800">
                                                Erreur de sauvegarde
                                            </h3>
                                            <div className="mt-2 text-sm text-red-700">
                                                {errors.backend}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                {/* Formulaire principal avec titre */}
                                <div className="space-y-6">
                                    <h3 className="text-xl font-medium text-gray-900 mb-4 tracking-tight">
                                        Descriptif de l'objectif
                                    </h3>
                                    <ObjectiveForm
                                        formData={formData}
                                        errors={errors}
                                        isSubmitting={isSubmitting}
                                        categoryOptions={categoryOptions}
                                        priorityOptions={priorityOptions}
                                        selectedCategory={selectedCategory}
                                        onInputChange={handleInputChange}
                                        onSubmit={handleSubmit}
                                        onClose={onClose}
                                        editMode={!!editObjectif}
                                        hideCategorySelector={true}
                                    />
                                </div>

                                {/* Panel droit */}
                                <div className="space-y-6">
                                    {/* Suggestions d'objectifs (seulement en mode création) */}
                                    {!editObjectif && (
                                        <div>
                                            <h3 className="text-xl font-medium text-gray-900 mb-4 tracking-tight">
                                                Objectifs Suggérés
                                                {loadingSuggestions && (
                                                    <span className="ml-2 text-sm text-gray-500 font-normal">(Chargement...)</span>
                                                )}
                                            </h3>
                                            <ObjectiveSuggestions
                                                formData={formData}
                                                categorySuggestions={categorySuggestions}
                                                loadingSuggestions={loadingSuggestions}
                                                errorSuggestions={errorSuggestions}
                                                selectedCategory={selectedCategory}
                                                isSubmitting={isSubmitting}
                                                onApplySuggestion={applySuggestion}
                                            />
                                        </div>
                                    )}

                                    {/* Zone d'information en mode édition */}
                                    {editObjectif && (
                                        <div>
                                            <h3 className="text-xl font-medium text-gray-900 mb-4 tracking-tight">
                                                Informations sur l'objectif
                                            </h3>
                                            <div className="p-6 bg-gray-50 rounded-lg space-y-3 text-sm text-gray-600">
                                                <div className="flex justify-between">
                                                    <span className="font-medium text-gray-700">Créé le :</span>
                                                    <span>{new Date(editObjectif.created_at).toLocaleDateString('fr-FR')}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="font-medium text-gray-700">Dernière mise à jour :</span>
                                                    <span>{new Date(editObjectif.updated_at).toLocaleDateString('fr-FR')}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="font-medium text-teal-600">Progression actuelle :</span>
                                                    <span className="font-medium text-teal-600">{editObjectif.progress_percentage?.toFixed(1) || 0}%</span>
                                                </div>
                                                {editObjectif.target_date && (
                                                    <div className="flex justify-between">
                                                        <span className="font-medium text-gray-700">Date cible :</span>
                                                        <span>{new Date(editObjectif.target_date).toLocaleDateString('fr-FR')}</span>
                                                    </div>
                                                )}
                                                <div className="flex justify-between items-center">
                                                    <span className="font-medium text-gray-700">Priorité :</span>
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => handleInputChange('priority', 'low')}
                                                            disabled={isSubmitting}
                                                            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors disabled:opacity-50 ${
                                                                formData.priority === 'low'
                                                                    ? 'bg-gray-700 text-white'
                                                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-400'
                                                            }`}
                                                        >
                                                            Basse
                                                        </button>
                                                        <button
                                                            onClick={() => handleInputChange('priority', 'medium')}
                                                            disabled={isSubmitting}
                                                            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors disabled:opacity-50 ${
                                                                formData.priority === 'medium'
                                                                    ? 'bg-gray-700 text-white'
                                                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-400'
                                                            }`}
                                                        >
                                                            Moyenne
                                                        </button>
                                                        <button
                                                            onClick={() => handleInputChange('priority', 'high')}
                                                            disabled={isSubmitting}
                                                            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors disabled:opacity-50 ${
                                                                formData.priority === 'high'
                                                                    ? 'bg-gray-700 text-white'
                                                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-400'
                                                            }`}
                                                        >
                                                            Haute
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span className="font-medium text-gray-700">Statut :</span>
                                                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${mapStatusDisplay(editObjectif.status).color}`}>
                                                        {mapStatusDisplay(editObjectif.status).label}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Message d'attente si aucune catégorie sélectionnée */}
                {!formData.category && (
                    <div className="flex-1 flex items-center justify-center">
                        <div className="text-center text-gray-500 pb-8">
                            <p className="text-lg mb-2">Sélectionnez une catégorie pour commencer</p>
                            <p className="text-sm">Choisissez le type d'objectif que vous souhaitez créer</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ObjectivesModal;